import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * The last page in the app that wasn't Your settings (KEY-768). Connecting Google or Outlook is a full-page round
 * trip through their sign-in, so on the way back the browser's history behind Your settings is Microsoft's or
 * Google's pages, not the dial the person came from. This remembers that dial for the session, so Back can return to
 * it and Your settings can offer to show a newly connected calendar there (KEY-769).
 */
const KEY = "kd.lastPlace";
const SETTINGS = "/settings";

export function rememberPlace(path: string, storage: Pick<Storage, "setItem"> | undefined = safeSession()): void {
  if (!storage || path === SETTINGS || path.startsWith(`${SETTINGS}?`) || !path.startsWith("/")) return;
  try {
    storage.setItem(KEY, path);
  } catch {
    // Private mode or storage blocked: Back falls back to My Dials.
  }
}

export function lastPlace(storage: Pick<Storage, "getItem"> | undefined = safeSession()): string | null {
  try {
    const path = storage?.getItem(KEY) ?? null;
    // Only ever an in-app path: never something that could send the browser off-site.
    return path && path.startsWith("/") && !path.startsWith("//") ? path : null;
  } catch {
    return null;
  }
}

/** The dial a remembered path belongs to, if it is a dial page (`/instances/<id>` or one of its settings pages). */
export function dialIdOf(path: string | null): string | null {
  const m = path ? /^\/instances\/([^/?#]+)/.exec(path) : null;
  return m ? decodeURIComponent(m[1]) : null;
}

/** Records every in-app page except Your settings as the place to come back to. */
export function useRememberLastPlace(): void {
  const location = useLocation();
  useEffect(() => {
    rememberPlace(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);
}

function safeSession(): Storage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.sessionStorage;
  } catch {
    return undefined;
  }
}

/** Mount once inside the router, beside the routes. */
export function LastPlaceTracker(): null {
  useRememberLastPlace();
  return null;
}
